import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  message,
} from 'antd';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import tradeCommitmentService from '../../../../services/tradeCommitmentService';
import purchaseDealSheetService from '../../../../services/purchaseDealSheetService';
import investorOnboardingService from '../../../../services/investorOnboardingService';
import AsyncEntitySelect from '../purchase-deal-sheet/AsyncEntitySelect';
import CounterpartyCreatableSelect from './CounterpartyCreatableSelect';
import { apiErrMsg } from './tradeCommitment.helpers';
import {
  COUNTERPARTY_TYPE_OPTIONS,
  DIRECTION_OPTIONS,
  PARTNER_SEARCH_MIN,
  PRODUCT_SEARCH_MIN,
  SUPPLIER_SEARCH_MIN,
  TRADE_MODE_OPTIONS,
} from './tradeCommitment.constants';

function resolveLegCounterparty(selected) {
  if (!selected?.name?.trim()) return null;
  return {
    counterparty_name: selected.name.trim(),
    counterparty_id:
      selected.custom ? undefined : (selected.uuid ?? selected.id),
  };
}

export default function TradeCommitmentCreatePage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedBuyCounterparty, setSelectedBuyCounterparty] = useState(null);
  const [selectedSellCounterparty, setSelectedSellCounterparty] = useState(null);
  const [selectedSingleCounterparty, setSelectedSingleCounterparty] = useState(null);

  const tradeMode = Form.useWatch('trade_mode', form) || 'spread';
  const direction = Form.useWatch('direction', form);
  const counterpartyType = Form.useWatch('counterparty_type', form);

  useEffect(() => {
    form.setFieldsValue({ trade_mode: 'spread' });
  }, [form]);

  useEffect(() => {
    if (tradeMode !== 'single') return;
    if (direction === 'SELL') {
      form.setFieldValue('counterparty_type', 'PARTNER');
    } else if (direction === 'BUY') {
      form.setFieldValue('counterparty_type', 'SUPPLIER');
    }
    form.setFieldValue('counterparty_id', undefined);
    setSelectedSingleCounterparty(null);
  }, [direction, tradeMode, form]);

  useEffect(() => {
    if (tradeMode !== 'single') return;
    form.setFieldValue('counterparty_id', undefined);
    setSelectedSingleCounterparty(null);
  }, [counterpartyType, tradeMode, form]);

  const fetchProducts = useCallback(
    (search) => purchaseDealSheetService.listProducts({ search, page: 1, limit: 50 }),
    [],
  );

  const fetchPartners = useCallback(async (search) => {
    const users = await investorOnboardingService.listIFAs({
      search,
      page: 1,
      limit: 20,
      status: 'active',
    });
    return {
      items: users.map((u) => ({
        id: u.id,
        uuid: u.uuid,
        name: u.name,
      })),
    };
  }, []);

  const fetchSuppliers = useCallback(
    (search) => purchaseDealSheetService.listSuppliers({ search, page: 1, limit: 20 }),
    [],
  );

  const handleSubmit = async (values) => {
    if (!selectedProduct) {
      message.error('Select a product');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (tradeMode === 'spread') {
        const buy = resolveLegCounterparty(selectedBuyCounterparty);
        const sell = resolveLegCounterparty(selectedSellCounterparty);

        if (!buy) {
          message.error('Select or enter a supplier (buy leg)');
          setLoading(false);
          return;
        }
        if (!sell) {
          message.error('Select or enter a partner (sell leg)');
          setLoading(false);
          return;
        }

        const result = await tradeCommitmentService.createSpread({
          product_id: selectedProduct.uuid ?? selectedProduct.id,
          quantity: values.quantity,
          notes: values.notes,
          source: 'ADMIN',
          buy: {
            price_per_share: values.buy_price_per_share,
            ...buy,
          },
          sell: {
            price_per_share: values.sell_price_per_share,
            ...sell,
          },
        });

        message.success(`Spread logged: ${result.legs?.map((l) => l.ref).join(' + ') || '2 legs'}`);
        navigate(`/pre-ipo/trade-commitments/${result.legs?.[0]?.id}`);
        return;
      }

      const useFreeTextOnly = counterpartyType === 'OTHER';
      const counterpartyName = useFreeTextOnly
        ? values.counterparty_name?.trim()
        : selectedSingleCounterparty?.name?.trim() || values.counterparty_name?.trim();

      if (!counterpartyName) {
        message.error('Enter or select a counterparty');
        return;
      }

      const payload = {
        direction: values.direction,
        product_id: selectedProduct.uuid ?? selectedProduct.id,
        quantity: values.quantity,
        price_per_share: values.price_per_share,
        counterparty_type: values.counterparty_type,
        counterparty_name: counterpartyName,
        notes: values.notes,
        source: 'ADMIN',
      };

      if (!useFreeTextOnly && selectedSingleCounterparty && !selectedSingleCounterparty.custom) {
        payload.counterparty_id =
          selectedSingleCounterparty.uuid ?? selectedSingleCounterparty.id;
      }

      const created = await tradeCommitmentService.create(payload);
      message.success('Trade commitment created');
      navigate(`/pre-ipo/trade-commitments/${created.id}`);
    } catch (err) {
      setError(apiErrMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const showSingleCounterpartySearch =
    counterpartyType === 'PARTNER' || counterpartyType === 'SUPPLIER';

  return (
    <DashboardPageWrapper title="New Trade Commitment">
      {error && <Alert type="error" message={error} className="mb-4" showIcon />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          trade_mode: 'spread',
          direction: 'SELL',
          counterparty_type: 'PARTNER',
        }}
        style={{ maxWidth: 720 }}
      >
        <Form.Item name="trade_mode" label="Deal type">
          <Segmented options={TRADE_MODE_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="product_id"
          label="Product"
          rules={[{ required: true, message: 'Select a product' }]}
        >
          <AsyncEntitySelect
            minSearchLength={PRODUCT_SEARCH_MIN}
            placeholder="Search by company name or ticker"
            searchHint="Type to search products"
            fetchOptions={fetchProducts}
            onSelectItem={setSelectedProduct}
          />
        </Form.Item>

        <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
          <InputNumber min={1} className="w-full" />
        </Form.Item>

        {tradeMode === 'spread' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Card title="Buy from (supplier)" size="small">
              <Form.Item
                name="buy_price_per_share"
                label="Buy price"
                rules={[{ required: true, message: 'Enter buy price' }]}
              >
                <InputNumber min={0.01} step={0.01} className="w-full" placeholder="e.g. 1900" />
              </Form.Item>
              <Form.Item
                name="buy_counterparty_id"
                label="Supplier"
                rules={[{ required: true, message: 'Select or enter supplier' }]}
              >
                <CounterpartyCreatableSelect
                  minSearchLength={SUPPLIER_SEARCH_MIN}
                  placeholder="Search supplier or use custom name"
                  searchHint={`Type at least ${SUPPLIER_SEARCH_MIN} characters to search`}
                  fetchOptions={fetchSuppliers}
                  onSelectItem={setSelectedBuyCounterparty}
                />
              </Form.Item>
            </Card>

            <Card title="Sell to (partner)" size="small">
              <Form.Item
                name="sell_price_per_share"
                label="Sell price"
                rules={[{ required: true, message: 'Enter sell price' }]}
              >
                <InputNumber min={0.01} step={0.01} className="w-full" placeholder="e.g. 1920" />
              </Form.Item>
              <Form.Item
                name="sell_counterparty_id"
                label="Partner / buyer"
                rules={[{ required: true, message: 'Select or enter partner' }]}
              >
                <CounterpartyCreatableSelect
                  minSearchLength={PARTNER_SEARCH_MIN}
                  placeholder="Search partner or use custom name"
                  searchHint={`Type at least ${PARTNER_SEARCH_MIN} characters to search`}
                  fetchOptions={fetchPartners}
                  onSelectItem={setSelectedSellCounterparty}
                />
              </Form.Item>
            </Card>
          </div>
        ) : (
          <>
            <Form.Item name="direction" label="Direction" rules={[{ required: true }]}>
              <Select options={DIRECTION_OPTIONS} />
            </Form.Item>

            <Form.Item
              name="price_per_share"
              label="Price per share"
              rules={[{ required: true }]}
            >
              <InputNumber min={0.01} step={0.01} className="w-full" />
            </Form.Item>

            <Form.Item name="counterparty_type" label="Counterparty type">
              <Select options={COUNTERPARTY_TYPE_OPTIONS} />
            </Form.Item>

            {showSingleCounterpartySearch ? (
              <Form.Item
                name="counterparty_id"
                label={counterpartyType === 'SUPPLIER' ? 'Supplier' : 'Partner'}
                rules={[{ required: true, message: 'Select or enter a counterparty' }]}
              >
                <CounterpartyCreatableSelect
                  key={counterpartyType}
                  minSearchLength={
                    counterpartyType === 'SUPPLIER' ? SUPPLIER_SEARCH_MIN : PARTNER_SEARCH_MIN
                  }
                  placeholder={
                    counterpartyType === 'SUPPLIER'
                      ? 'Search supplier or use custom name'
                      : 'Search partner or use custom name'
                  }
                  searchHint={
                    counterpartyType === 'SUPPLIER'
                      ? `Type at least ${SUPPLIER_SEARCH_MIN} characters to search`
                      : `Type at least ${PARTNER_SEARCH_MIN} characters to search`
                  }
                  fetchOptions={
                    counterpartyType === 'SUPPLIER' ? fetchSuppliers : fetchPartners
                  }
                  onSelectItem={setSelectedSingleCounterparty}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="counterparty_name"
                label="Counterparty name"
                rules={[{ required: true, message: 'Enter counterparty name' }]}
              >
                <Input placeholder="Enter counterparty name" />
              </Form.Item>
            )}
          </>
        )}

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="WhatsApp deal reference, UTR, etc." />
        </Form.Item>

        <div className="flex gap-2">
          <Button type="primary" htmlType="submit" loading={loading}>
            {tradeMode === 'spread' ? 'Create spread' : 'Create'}
          </Button>
          <Button onClick={() => navigate('/pre-ipo/trade-commitments')}>Cancel</Button>
        </div>
      </Form>
    </DashboardPageWrapper>
  );
}
